import {
  useCallback,
  useEffect,
  useRef,
  type KeyboardEvent as ReactKeyboardEvent,
  type RefObject,
} from 'react';

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

/** The focusable descendants of a container, in document order. */
function focusableWithin(container: HTMLElement): HTMLElement[] {
  return [...container.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(
    (element) => element.offsetParent !== null || element.getClientRects().length > 0,
  );
}

/**
 * Keeps keyboard focus inside a container while it is open.
 *
 * `aria-modal` tells a screen reader that the rest of the page is inert; it
 * does nothing at all for the Tab key. Without a trap, a keyboard user tabs
 * straight out of an open dialog into the page behind it — which is still
 * rendered, still clickable, and now unreachable by anyone using a pointer.
 * This hook is what makes `aria-modal` honest.
 *
 * It also restores focus to whatever was focused before the dialog opened, so
 * dismissing a dialog returns the user to the control that summoned it rather
 * than to the top of the document.
 *
 * Escape is handled by the returned React handler, which the caller attaches as
 * the container's `onKeyDown`, rather than by a native listener. A native
 * listener on the container runs before React dispatches to the widgets inside
 * it, so an open date picker or combobox could not keep Escape to itself and
 * one press closed the whole dialog.
 *
 * @param containerRef the element to trap focus inside
 * @param active whether the trap is engaged
 * @param onEscape called when Escape is pressed inside the container and nothing inside consumed it
 * @returns the container's `onKeyDown` handler
 */
export function useFocusTrap(
  containerRef: RefObject<HTMLElement | null>,
  active: boolean,
  onEscape: () => void,
): (event: ReactKeyboardEvent) => void {
  const escapeRef = useRef(onEscape);

  // Keep the event handler installed for the lifetime of this trap, while
  // still invoking the callback from the latest render. Modal callbacks often
  // close over state that changes as a user types; rebuilding the trap for
  // every callback identity change steals focus from the edited control.
  useEffect(() => {
    escapeRef.current = onEscape;
  }, [onEscape]);

  useEffect(() => {
    const container = containerRef.current;
    if (!active || container === null) {
      return;
    }

    const previouslyFocused = document.activeElement as HTMLElement | null;
    const initial = focusableWithin(container)[0] ?? container;
    initial.focus();

    function handleKeyDown(event: KeyboardEvent): void {
      if (container === null) {
        return;
      }
      if (event.key !== 'Tab') {
        return;
      }

      const focusable = focusableWithin(container);
      if (focusable.length === 0) {
        event.preventDefault();
        container.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const current = document.activeElement;

      if (event.shiftKey && (current === first || !container.contains(current))) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && current === last) {
        event.preventDefault();
        first?.focus();
      }
    }

    container.addEventListener('keydown', handleKeyDown);
    return () => {
      container.removeEventListener('keydown', handleKeyDown);
      // React removes the container from the document before this cleanup runs,
      // which leaves focus on <body>. That — or focus still being inside the
      // container — means nothing else has claimed it, so it is ours to give
      // back. If something else has taken focus in the meantime, leave it alone.
      const active = document.activeElement;
      if (active === null || active === document.body || container.contains(active)) {
        previouslyFocused?.focus();
      }
    };
  }, [containerRef, active]);

  return useCallback(
    (event: ReactKeyboardEvent) => {
      if (!active || event.key !== 'Escape' || event.defaultPrevented) {
        return;
      }
      event.stopPropagation();
      escapeRef.current();
    },
    [active],
  );
}
